import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject, interval } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppError } from 'src/app/core/models/api-error';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';
import { secondsUntilOdooDateTime } from 'src/app/shared/utils/helpers';

import {
  MfaFactor,
  MfaMethod,
  MfaRecoveryState,
  MfaStatus,
} from '../../models/mfa.models';
import { MfaService } from '../../services/mfa.service';

type PageStep = 'status' | 'enrolling' | 'done';

/**
 * Per-user two-step verification: review, enrol, turn off.
 *
 * State is local, not NgRx: the TOTP seed is shown once and read by nothing
 * else, and a global store would leave it in the devtools timeline. The
 * enrolment steps themselves belong to `MfaEnrolmentWizardComponent`, which
 * the prompt shown after login mounts too.
 */
@Component({
  selector: 'app-security-page',
  templateUrl: './security-page.component.html',
  styleUrls: ['./security-page.component.scss'],
})
export class SecurityPageComponent implements OnInit, OnDestroy {
  step: PageStep = 'status';
  /** The method the wizard is enrolling, while one is mounted. */
  method: MfaMethod = 'totp';

  status: MfaStatus | null = null;
  error: AppError | null = null;
  loading = false;
  /** The status call failed, so a null `status` does not mean "no factors" --
   *  a blip used to tell an enrolled user they had none. */
  statusFailed = false;

  /** Step-up code for turning a factor off. */
  disableForm!: FormGroup;
  disabling: MfaMethod | null = null;
  /** A step-up code is being mailed for the turn-off form. */
  sendingStepUpCode = false;
  /** Server-side throttle on that send, echoed back for the button. */
  stepUpNextAllowedAt: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly mfa: MfaService,
    private readonly errorMessages: ErrorMessageService
  ) {}

  ngOnInit(): void {
    this.disableForm = this.fb.group({ code: [''] });

    // Only to tick change detection so the step-up countdown renders.
    // `stepUpResendWait` is computed on read, or it would sit a second stale
    // after every send.
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe();

    this.loadStatus();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- status ------------------------------------------------------------

  loadStatus(): void {
    this.loading = true;
    this.mfa
      .status()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.status = response.object ?? null;
          this.statusFailed = false;
        },
        error: (error) => {
          this.statusFailed = true;
          this.fail(error);
        },
      });
  }

  get factors(): MfaFactor[] {
    return this.status?.factors ?? [];
  }

  factorFor(method: MfaMethod): MfaFactor | null {
    return this.factors.find((factor) => factor.method === method) ?? null;
  }

  isActive(method: MfaMethod): boolean {
    return this.factorFor(method)?.state === 'active';
  }

  /** Email codes are only on offer where the tenant has switched them on. */
  get emailAvailable(): boolean {
    return !!this.status?.email_otp_available;
  }

  /**
   * Recovery-code counts off the status response. Null until it lands, and
   * against a backend predating the field -- which the panel renders as
   * "none yet" rather than zero left.
   */
  get recovery(): MfaRecoveryState | null {
    return this.status?.recovery ?? null;
  }

  // --- enrolment ---------------------------------------------------------

  /** Mounts the wizard, which starts the enrolment itself. */
  startEnrolment(method: MfaMethod): void {
    this.error = null;
    this.method = method;
    this.step = 'enrolling';
  }

  /** The wizard hands back the status its confirmation returned. */
  onEnrolled(status: MfaStatus | null): void {
    this.status = status ?? this.status;
    this.statusFailed = false;
    this.step = 'done';
  }

  cancelEnrolment(): void {
    this.error = null;
    this.step = 'status';
    this.loadStatus();
  }

  // --- disabling ---------------------------------------------------------

  /** A pending factor was never proved, so no step-up is asked for. */
  startDisable(method: MfaMethod): void {
    this.error = null;
    this.disabling = method;
    this.disableForm.reset({ code: '' });
  }

  cancelDisable(): void {
    this.disabling = null;
    this.disableForm.reset({ code: '' });
  }

  get stepUpRequired(): boolean {
    return this.factors.some((factor) => factor.state === 'active');
  }

  /** An email factor is active, so a step-up code can be mailed. The only
   *  route to one for an email-OTP-only user: re-enrolling email 409s. */
  get canSendStepUpCode(): boolean {
    return this.isActive('email');
  }

  /** Seconds until another step-up code may be sent; 0 when one may be now. */
  get stepUpResendWait(): number {
    return secondsUntilOdooDateTime(this.stepUpNextAllowedAt);
  }

  sendStepUpCode(): void {
    if (this.sendingStepUpCode || this.stepUpResendWait > 0) {
      return;
    }
    this.error = null;
    this.sendingStepUpCode = true;
    this.mfa
      .sendStepUpCode()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.sendingStepUpCode = false))
      )
      .subscribe({
        next: (response) =>
          (this.stepUpNextAllowedAt = response.object?.next_allowed_at ?? null),
        error: (error) => this.fail(error),
      });
  }

  confirmDisable(): void {
    if (!this.disabling) {
      return;
    }
    const code = (this.disableForm.controls['code'].value as string) || '';
    this.error = null;
    this.loading = true;
    this.mfa
      .disable(this.disabling, code || undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.status = response.object ?? this.status;
          this.cancelDisable();
        },
        error: (error) => this.fail(error),
      });
  }

  // ui-form-field preventDefaults Enter, killing the implicit submit, so the
  // form needs a bridge. Inputs only: a button already turns Enter into a click.
  onEnterDisable(event: Event): void {
    if (SecurityPageComponent.fromInput(event)) {
      this.confirmDisable();
    }
  }

  private static fromInput(event: Event): boolean {
    return (event.target as HTMLElement | null)?.tagName === 'INPUT';
  }

  private fail(error: unknown): void {
    this.error = this.errorMessages.parse(error as never);
  }
}
